import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  Table,
  Tag,
  message,
  Modal,
  Input,
  Alert,
  Spin,
  Popconfirm,
  Tabs,
  Divider,
  List,
  Badge,
  Tooltip,
} from 'antd';
import {
  SafetyCertificateOutlined,
  LaptopOutlined,
  MobileOutlined,
  ReloadOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CopyOutlined,
  QrcodeOutlined,
  KeyOutlined,
  GlobalOutlined,
  DesktopOutlined,
  TabletOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { mfaService, sessionService } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const SecuritySettings = () => {
  // MFA State
  const [mfaStatus, setMfaStatus] = useState(null);
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disableCode, setDisableCode] = useState('');
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');

  // Sessions State
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [mfaData, sessionData, currentSess] = await Promise.all([
        mfaService.getStatus(),
        sessionService.getMySessions(),
        sessionService.getCurrentSession().catch(() => null),
      ]);
      setMfaStatus(mfaData);
      setSessions(sessionData.sessions || []);
      setCurrentSession(currentSess);
    } catch (error) {
      message.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  // MFA Functions
  const handleStartEnrollment = async () => {
    setEnrolling(true);
    try {
      const data = await mfaService.startEnrollment();
      setEnrollmentData(data);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to start MFA enrollment');
    } finally {
      setEnrolling(false);
    }
  };

  const handleVerifyEnrollment = async () => {
    if (!verifyCode || verifyCode.length < 6) {
      message.error('Please enter a valid 6-digit code');
      return;
    }

    try {
      const result = await mfaService.verifyEnrollment(verifyCode);
      setRecoveryCodes(result.codes);
      setEnrollmentData(null);
      setVerifyCode('');
      message.success('MFA enabled successfully');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Invalid verification code');
    }
  };

  const handleCancelEnrollment = () => {
    setEnrollmentData(null);
    setVerifyCode('');
  };

  const handleDisableMFA = async () => {
    try {
      await mfaService.disable(disableCode);
      message.success('MFA disabled');
      setShowDisableModal(false);
      setDisableCode('');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Invalid verification code');
    }
  };

  const handleRegenerateCodes = async () => {
    try {
      const result = await mfaService.regenerateRecoveryCodes(regenerateCode);
      setRecoveryCodes(result.codes);
      setShowRegenerateModal(false);
      setRegenerateCode('');
      message.success('Recovery codes regenerated');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Invalid verification code');
    }
  };

  const copyRecoveryCodes = () => {
    if (recoveryCodes) {
      navigator.clipboard.writeText(recoveryCodes.join('\n'));
      message.success('Recovery codes copied to clipboard');
    }
  };

  const handleCloseRecoveryCodes = () => {
    setRecoveryCodes(null);
  };

  // Session Functions
  const handleRevokeSession = async (sessionId) => {
    try {
      await sessionService.revokeSession(sessionId);
      message.success('Session revoked');
      fetchData();
    } catch (error) {
      message.error('Failed to revoke session');
    }
  };

  const handleRevokeAllOther = async () => {
    try {
      const result = await sessionService.revokeAllOther();
      message.success(`Revoked ${result.revoked_count} sessions`);
      fetchData();
    } catch (error) {
      message.error('Failed to revoke sessions');
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <MobileOutlined />;
      case 'tablet':
        return <TabletOutlined />;
      case 'pc':
      case 'desktop':
        return <DesktopOutlined />;
      default:
        return <LaptopOutlined />;
    }
  };

  const sessionColumns = [
    {
      title: 'Device',
      key: 'device',
      render: (_, record) => (
        <Space>
          {getDeviceIcon(record.device_type)}
          <span>
            {record.browser || 'Unknown Browser'}
            {record.os && ` on ${record.os}`}
          </span>
          {record.is_current && <Tag color="green">Current</Tag>}
        </Space>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <Space>
          <GlobalOutlined />
          <span>{record.location_country || record.ip_address || 'Unknown'}</span>
        </Space>
      ),
    },
    {
      title: 'Last Active',
      dataIndex: 'last_activity_at',
      key: 'last_activity_at',
      render: (date) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: 'Auth Method',
      dataIndex: 'auth_method',
      key: 'auth_method',
      render: (method) => (
        <Tag color={method === 'password' ? 'blue' : method === 'saml' ? 'purple' : 'cyan'}>
          {method?.toUpperCase() || 'PASSWORD'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        record.is_current ? (
          <Text type="secondary">Current session</Text>
        ) : (
          <Popconfirm
            title="Revoke this session?"
            description="The user will be logged out on this device."
            onConfirm={() => handleRevokeSession(record.session_id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              Revoke
            </Button>
          </Popconfirm>
        )
      ),
    },
  ];

  // MFA Tab Content
  const renderMFAContent = () => {
    if (loading) {
      return <Spin />;
    }

    // Show recovery codes if just generated
    if (recoveryCodes) {
      return (
        <Card>
          <Alert
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            message="Save Your Recovery Codes"
            description="Store these codes in a safe place. You can use each code once if you lose access to your authenticator app."
            style={{ marginBottom: 16 }}
          />
          <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <List
              grid={{ gutter: 16, column: 2 }}
              dataSource={recoveryCodes}
              renderItem={(code, index) => (
                <List.Item>
                  <Text code style={{ fontSize: 16 }}>
                    {index + 1}. {code}
                  </Text>
                </List.Item>
              )}
            />
          </div>
          <Space>
            <Button icon={<CopyOutlined />} onClick={copyRecoveryCodes}>
              Copy All Codes
            </Button>
            <Button type="primary" onClick={handleCloseRecoveryCodes}>
              I've Saved These Codes
            </Button>
          </Space>
        </Card>
      );
    }

    // Show enrollment flow
    if (enrollmentData) {
      return (
        <Card>
          <Title level={5}>
            <QrcodeOutlined /> Set Up Two-Factor Authentication
          </Title>
          <Paragraph>
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </Paragraph>

          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            {enrollmentData.qr_code_data_url ? (
              <img
                src={enrollmentData.qr_code_data_url}
                alt="MFA QR Code"
                style={{ maxWidth: 200, border: '1px solid #d9d9d9', padding: 8 }}
              />
            ) : (
              <Alert
                type="info"
                message="Manual Entry"
                description={
                  <Space direction="vertical">
                    <Text>If you can't scan the QR code, enter this key manually:</Text>
                    <Text code copyable>{enrollmentData.secret}</Text>
                  </Space>
                }
              />
            )}
          </div>

          <Divider>Enter Verification Code</Divider>

          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              placeholder="Enter 6-digit code"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              style={{ maxWidth: 200 }}
              size="large"
            />
            <Space>
              <Button type="primary" onClick={handleVerifyEnrollment} disabled={verifyCode.length !== 6}>
                Verify & Enable
              </Button>
              <Button onClick={handleCancelEnrollment}>Cancel</Button>
            </Space>
          </Space>
        </Card>
      );
    }

    // Show status
    return (
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Space>
            {mfaStatus?.is_enabled ? (
              <Badge status="success" />
            ) : (
              <Badge status="default" />
            )}
            <Title level={5} style={{ margin: 0 }}>
              Two-Factor Authentication
            </Title>
          </Space>
        </div>

        {mfaStatus?.is_enabled ? (
          <>
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="MFA is enabled"
              description={
                <Space direction="vertical">
                  <Text>Your account is protected with two-factor authentication.</Text>
                  {mfaStatus.recovery_codes_remaining !== undefined && (
                    <Text>
                      Recovery codes remaining: <strong>{mfaStatus.recovery_codes_remaining}</strong>
                    </Text>
                  )}
                </Space>
              }
              style={{ marginBottom: 16 }}
            />
            <Space>
              <Button
                icon={<KeyOutlined />}
                onClick={() => setShowRegenerateModal(true)}
              >
                Regenerate Recovery Codes
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => setShowDisableModal(true)}
              >
                Disable MFA
              </Button>
            </Space>
          </>
        ) : (
          <>
            <Paragraph>
              Two-factor authentication adds an extra layer of security to your account.
              When enabled, you'll need to enter a code from your authenticator app in
              addition to your password when signing in.
            </Paragraph>
            <Button
              type="primary"
              icon={<SafetyCertificateOutlined />}
              onClick={handleStartEnrollment}
              loading={enrolling}
            >
              Enable Two-Factor Authentication
            </Button>
          </>
        )}

        {/* Disable MFA Modal */}
        <Modal
          title="Disable Two-Factor Authentication"
          open={showDisableModal}
          onCancel={() => {
            setShowDisableModal(false);
            setDisableCode('');
          }}
          onOk={handleDisableMFA}
          okText="Disable MFA"
          okButtonProps={{ danger: true, disabled: disableCode.length !== 6 }}
        >
          <Alert
            type="warning"
            message="This will reduce your account security"
            style={{ marginBottom: 16 }}
          />
          <Paragraph>
            Enter your current authentication code to confirm:
          </Paragraph>
          <Input
            placeholder="Enter 6-digit code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            style={{ maxWidth: 200 }}
          />
        </Modal>

        {/* Regenerate Codes Modal */}
        <Modal
          title="Regenerate Recovery Codes"
          open={showRegenerateModal}
          onCancel={() => {
            setShowRegenerateModal(false);
            setRegenerateCode('');
          }}
          onOk={handleRegenerateCodes}
          okText="Regenerate"
          okButtonProps={{ disabled: regenerateCode.length !== 6 }}
        >
          <Alert
            type="info"
            message="This will invalidate your existing recovery codes"
            style={{ marginBottom: 16 }}
          />
          <Paragraph>
            Enter your current authentication code to confirm:
          </Paragraph>
          <Input
            placeholder="Enter 6-digit code"
            value={regenerateCode}
            onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            style={{ maxWidth: 200 }}
          />
        </Modal>
      </Card>
    );
  };

  // Sessions Tab Content
  const renderSessionsContent = () => {
    return (
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              <LaptopOutlined /> Active Sessions
            </Title>
            <Text type="secondary">
              Manage devices where you're currently signed in
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData}>
              Refresh
            </Button>
            {sessions.filter(s => !s.is_current).length > 0 && (
              <Popconfirm
                title="Revoke all other sessions?"
                description="You will be logged out on all other devices."
                onConfirm={handleRevokeAllOther}
              >
                <Button danger>
                  Revoke All Others
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>

        <Table
          columns={sessionColumns}
          dataSource={sessions}
          rowKey="session_id"
          loading={loading}
          pagination={false}
        />
      </Card>
    );
  };

  const tabItems = [
    {
      key: 'mfa',
      label: (
        <span>
          <SafetyCertificateOutlined />
          Two-Factor Auth
        </span>
      ),
      children: renderMFAContent(),
    },
    {
      key: 'sessions',
      label: (
        <span>
          <LaptopOutlined />
          Sessions
          <Badge count={sessions.length} style={{ marginLeft: 8 }} />
        </span>
      ),
      children: renderSessionsContent(),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Security Settings</Title>
        <Text type="secondary">Manage your account security and active sessions</Text>
      </div>

      <Tabs items={tabItems} />
    </div>
  );
};

export default SecuritySettings;
